"use client";

import { createContext, useContext, useEffect, useState } from "react";

type Theme = "light" | "dark" | "system";

interface ThemeContext {
  theme: Theme;
  resolvedTheme: "light" | "dark";
  setTheme: (t: Theme) => void;
}

const ThemeCtx = createContext<ThemeContext>({
  theme: "system",
  resolvedTheme: "light",
  setTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeCtx);
}

/**
 * Resolve a theme preference to a concrete "light" | "dark" value.
 * "system" follows `prefers-color-scheme`. SSR-safe: returns "light"
 * if window/matchMedia isn't available yet.
 */
function resolveTheme(t: Theme): "light" | "dark" {
  if (t !== "system") return t;
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Read the stored theme from localStorage. SSR-safe.
 */
function getStoredTheme(): Theme {
  // Default is LIGHT (white) for everyone. Dark only when the user explicitly
  // toggles it (persisted in localStorage). We no longer follow the OS
  // "system" preference by default — white is the intended look.
  if (typeof window === "undefined") return "light";
  const stored = localStorage.getItem("yofi-theme") as Theme | null;
  if (stored === "light" || stored === "dark") return stored;
  return "light";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Lazy-init from localStorage on mount so we don't have to call setState
  // inside an effect. The function form runs only once (client-side).
  const [theme, setThemeState] = useState<Theme>(getStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">(() => resolveTheme(getStoredTheme()));

  // Apply theme class to <html> and listen for system changes.
  useEffect(() => {
    const root = document.documentElement;
    const apply = (r: "light" | "dark") => {
      root.classList.toggle("dark", r === "dark");
      root.classList.toggle("light", r === "light");
      setResolvedTheme(r);
    };

    apply(resolveTheme(theme));

    if (theme === "system") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = (e: MediaQueryListEvent) => apply(e.matches ? "dark" : "light");
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
  }, [theme]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    localStorage.setItem("yofi-theme", t);
  };

  return (
    <ThemeCtx.Provider value={{ theme, resolvedTheme, setTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}
